import { useEffect, useState } from 'react';
import { supabase, handleSupabaseError } from '../lib/supabase';
import type { BookIdea } from './useBookIdeas';
import type { SocialLinks } from '../components/SocialLinks';

export type Profile = {
  id: string;
  username: string;
  full_name: string | null;
  avatar_url: string | null;
  background_image_url: string | null;
  role: 'reader' | 'author' | 'admin';
  bio: string | null;
  website: string | null;
  social_links: SocialLinks;
  created_at: string;
  book_ideas?: BookIdea[];
};

export function useProfile(profileIdOrSlug: string) {
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [profileId, setProfileId] = useState<string | null>(null);

  // First, resolve the profile ID if we're given a slug
  useEffect(() => {
    const resolveProfileId = async () => {
      try {
        // Check if the input is a UUID
        const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
        if (uuidRegex.test(profileIdOrSlug)) {
          setProfileId(profileIdOrSlug);
          return;
        }

        // Look up the profile ID from the slug
        const { data: resolvedId, error: lookupError } = await supabase
          .rpc('find_profile_by_slug', {
            search_slug: profileIdOrSlug
          });

        if (lookupError) throw lookupError;
        setProfileId(resolvedId);
      } catch (error) {
        console.error('Error resolving profile ID:', error);
        setProfileId(null);
      }
    };

    resolveProfileId();
  }, [profileIdOrSlug]);

  // Then fetch profile data once we have the ID
  useEffect(() => {
    if (!profileId) return;
    fetchProfile();
  }, [profileId]);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select(`
          id,
          username,
          full_name,
          avatar_url,
          background_image_url,
          role,
          bio,
          website,
          social_links,
          created_at
        `)
        .eq('id', profileId)
        .single();

      if (profileError) {
        if (profileError.code === 'PGRST116') {
          setError('Profile not found');
        } else {
          throw profileError;
        }
        return;
      }

      // Get only book ideas where identity_id matches the profile id
      const { data: ideasData, error: ideasError } = await supabase
        .from('book_ideas')
        .select(`
          *,
          votes:votes(count),
          comments:comments(count),
          followers:idea_follows(count),
          has_published_milestone:milestones!inner(status)
        `)
        .eq('author_id', profileId)
        .eq('identity_id', profileId)
        .order('created_at', { ascending: false });

      if (ideasError) throw ideasError;

      const bookIdeas = ideasData
        .map(idea => ({
          ...idea,
          votes_count: idea.votes[0]?.count ?? 0,
          comments_count: idea.comments[0]?.count ?? 0,
          followers_count: idea.followers[0]?.count ?? 0,
          has_published_milestone: idea.has_published_milestone?.some(m => m.status === 'completed') ?? false
        }))
        .sort((a, b) => {
          if (a.has_published_milestone === b.has_published_milestone) {
            return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
          }
          return a.has_published_milestone ? 1 : -1;
        });

      setProfile({
        ...profileData,
        book_ideas: bookIdeas
      });
    } catch (err) {
      setError(handleSupabaseError(err));
      console.error('Error fetching profile:', err);
    } finally {
      setLoading(false);
    }
  };

  const updateProfile = async (updates: Partial<Omit<Profile, 'id' | 'created_at' | 'book_ideas'>>) => {
    if (!profileId) return;

    try {
      const { error: updateError } = await supabase
        .from('profiles')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', profileId);

      if (updateError) throw updateError;

      // Refresh profile data
      const { data: updatedProfile, error: fetchError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', profileId)
        .single();

      if (fetchError) throw fetchError;

      setProfile(prev => prev ? { ...prev, ...updatedProfile } : updatedProfile);
    } catch (err) {
      throw new Error(handleSupabaseError(err));
    }
  };

  return { 
    profile, 
    loading: loading || !profileId, 
    error, 
    updateProfile 
  };
}